import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { collection, getDocs, query, orderBy, Timestamp } from 'firebase/firestore';
import { getAllUsers, updateUserProfile, updateUserSubscription, updateUserHistoryPreference } from '../services/userService';
import './UserManagementPage.css';

const UserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [subDuration, setSubDuration] = useState({ value: 1, unit: 'months' });
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const allUsers = await getAllUsers();
            setUsers(allUsers);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await updateUserProfile(userId, { role: newRole });
            setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
        } catch (err) {
            alert('Failed to update role: ' + err.message);
        }
    };

    const handleHistoryChange = async (userId, currentPref) => {
        try {
            const newPref = !currentPref;
            await updateUserHistoryPreference(userId, newPref);
            setUsers(users.map(u => u.id === userId ? { ...u, saveHistory: newPref } : u));
        } catch (err) {
            alert('Failed to update history preference: ' + err.message);
        }
    };

    const openSubscriptionModal = (user) => {
        setSelectedUser(user);
        setShowSubscriptionModal(true);
    };

    const handleUpdateSubscription = async () => {
        if (!selectedUser) return;
        try {
            const params = {};
            params[subDuration.unit] = parseInt(subDuration.value);
            await updateUserSubscription(selectedUser.id, params);

            // Update local state (optimistic or just re-fetch)
            fetchUsers();
            setShowSubscriptionModal(false);
            alert(`Subscription updated for ${selectedUser.email}`);
        } catch (err) {
            alert('Failed to update subscription: ' + err.message);
        }
    };

    const filteredUsers = users.filter(user =>
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatExpiry = (timestamp) => {
        if (!timestamp) return 'No active subscription';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    if (loading) return <div className="admin-loading">Loading Management Console...</div>;

    return (
        <div className="user-management-container">
            <div className="admin-header">
                <button className="back-btn" onClick={() => navigate('/admin')}>&larr; Back to Dashboard</button>
                <h1>User Management Console</h1>
                <div className="search-bar">
                    <input
                        type="text"
                        placeholder="Search by email or name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {error && <div className="error-banner">{error}</div>}

            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Role</th>
                            <th>History Tracking</th>
                            <th>Subscription Expiry</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => (
                            <tr key={user.id}>
                                <td>
                                    <div className="user-info">
                                        <span className="user-name">{user.displayName || 'Unnamed User'}</span>
                                        <span className="user-email">{user.email}</span>
                                    </div>
                                </td>
                                <td>
                                    <select
                                        value={user.role || 'free'}
                                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                        className={`role-select ${user.role}`}
                                    >
                                        <option value="free">Free</option>
                                        <option value="paid">Paid</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </td>
                                <td>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={!!user.saveHistory}
                                            onChange={() => handleHistoryChange(user.id, !!user.saveHistory)}
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                    <span className="history-status-text">
                                        {user.saveHistory ? ' ON' : ' OFF'}
                                    </span>
                                </td>
                                <td>
                                    <span className={`expiry-date ${user.subscriptionExpiresAt ? 'active' : ''}`}>
                                        {formatExpiry(user.subscriptionExpiresAt)}
                                    </span>
                                </td>
                                <td>
                                    <button
                                        className="manage-sub-btn"
                                        onClick={() => openSubscriptionModal(user)}
                                        disabled={user.role !== 'paid'}
                                    >
                                        Set Duration
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showSubscriptionModal && (
                <div className="modal-overlay">
                    <div className="subscription-modal">
                        <h2>Set Subscription Duration</h2>
                        <p>User: {selectedUser?.email}</p>
                        <div className="duration-inputs">
                            <input
                                type="number"
                                min="1"
                                value={subDuration.value}
                                onChange={(e) => setSubDuration({ ...subDuration, value: e.target.value })}
                            />
                            <select
                                value={subDuration.unit}
                                onChange={(e) => setSubDuration({ ...subDuration, unit: e.target.value })}
                            >
                                <option value="weeks">Weeks</option>
                                <option value="months">Months</option>
                                <option value="years">Years</option>
                            </select>
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setShowSubscriptionModal(false)}>Cancel</button>
                            <button className="confirm-btn" onClick={handleUpdateSubscription}>Update Access</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagementPage;
